import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { ThemeProvider } from './components/theme-provider';
import { AppHeader } from './components/layout/AppHeader';
import { Toaster } from './components/ui/toaster';
import { DashboardPage } from './pages/DashboardPage';
import { UploadPage } from './pages/UploadPage';
import { CropPage } from './pages/CropPage';
import { ProductionPage } from './pages/ProductionPage';
import { CompletePage } from './pages/CompletePage';
import { ProductPage } from './pages/ProductPage';
import { ProductsListPage } from './pages/ProductsListPage';
import { SettingsPage } from './components/SettingsPage';
import { LoginPage } from './pages/LoginPage';
import { AuthGuard } from './components/AuthGuard';
import { useProductWorkflow } from './hooks/useProductWorkflow';
import { getRouteForStep } from './constants/routes';
import type { AppStep } from './types/app';
import { useEffect } from 'react';

function AppLayout({ children, currentStep }: { children: React.ReactNode; currentStep: AppStep }) {
  const navigate = useNavigate();

  const handleNavigate = (step: AppStep) => {
    navigate(getRouteForStep(step));
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster />
      <AppHeader currentStep={currentStep} onNavigate={handleNavigate} />
      <main className="container mx-auto px-6 py-8 max-w-7xl">
        {children}
      </main>
    </div>
  );
}

function DashboardRoute() {
  const navigate = useNavigate();
  const handleNavigate = (step: AppStep) => {
    navigate(getRouteForStep(step));
  };

  return (
    <AppLayout currentStep="dashboard">
      <DashboardPage onNavigate={handleNavigate} />
    </AppLayout>
  );
}

function ProductsRoute() {
  const navigate = useNavigate();
  const handleNavigate = (step: AppStep) => {
    navigate(getRouteForStep(step));
  };

  return (
    <AppLayout currentStep="products">
      <ProductsListPage
        onNavigate={handleNavigate}
        onViewProduct={(product) => navigate(`/products/${product.id}`)}
      />
    </AppLayout>
  );
}

function ProductRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workflow = useProductWorkflow();

  useEffect(() => {
    if (id) {
      workflow.loadProductById(id);
    }
  }, [id, workflow.loadProductById]);

  if (!workflow.currentProduct) {
    return (
      <AppLayout currentStep="product">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading product...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentStep="product">
      <ProductPage
        product={workflow.currentProduct}
        onRedoCrop={workflow.currentProduct.masterImageUrl ? () => navigate('/crop') : undefined}
        onDeleted={() => navigate('/products')}
        onRenamed={(product) => workflow.navigateToProduct(product)}
        onProductUpdated={(product) => workflow.setCurrentProduct(product)}
      />
    </AppLayout>
  );
}

function UploadRoute() {
  return (
    <AppLayout currentStep="upload">
      <UploadPage />
    </AppLayout>
  );
}

function CropRoute() {
  return (
    <AppLayout currentStep="crop">
      <CropPage />
    </AppLayout>
  );
}

function ProductionRoute() {
  return (
    <AppLayout currentStep="production">
      <ProductionPage />
    </AppLayout>
  );
}

function CompleteRoute() {
  return (
    <AppLayout currentStep="complete">
      <CompletePage />
    </AppLayout>
  );
}

function SettingsRoute() {
  return (
    <AppLayout currentStep="settings">
      <SettingsPage />
    </AppLayout>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="etsyflow-ui-theme">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/" element={<AuthGuard><DashboardRoute /></AuthGuard>} />
          <Route path="/products" element={<AuthGuard><ProductsRoute /></AuthGuard>} />
          <Route path="/products/:id" element={<AuthGuard><ProductRoute /></AuthGuard>} />
          <Route path="/upload" element={<AuthGuard><UploadRoute /></AuthGuard>} />
          <Route path="/crop" element={<AuthGuard><CropRoute /></AuthGuard>} />
          <Route path="/production" element={<AuthGuard><ProductionRoute /></AuthGuard>} />
          <Route path="/complete" element={<AuthGuard><CompleteRoute /></AuthGuard>} />
          <Route path="/settings" element={<AuthGuard><SettingsRoute /></AuthGuard>} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;

